import { useChat } from '../../../../libs/hooks';
import { AlertType } from '../../../../libs/models/AlertType';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { IConversationDisplayMode } from '../../../../redux/features/app/AppState';
import { addAlert } from '../../../../redux/features/app/appSlice';
import { ConversationHiddenStatusChange } from '../../../../redux/features/conversations/ConversationsState';
import {
    setConversationAsHidden,
    setSelectedConversation,
} from '../../../../redux/features/conversations/conversationsSlice';
import { ModalDialog } from '../ChatWindow/Shared/ModalDialog';

interface IHideChatProps {
    chatId: string;
    chatName: string;
    open: boolean;
    onClose: () => void;
}

export const HideChatDialog: React.FC<IHideChatProps> = ({ chatName, chatId, open, onClose }) => {
    const dispatch = useAppDispatch();
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const { conversationDisplayMode } = useAppSelector((state: RootState) => state.app);
    const chat = useChat();

    const onHideChat = async () => {
        const chatState = conversations[chatId];
        await chat
            .editChat(chatId, chatName, chatState.memoryBalance, chatState.chatScope, [], true)
            .then(() => {
                dispatch(
                    setConversationAsHidden({ id: chatId, newHiddenState: true } as ConversationHiddenStatusChange),
                );
                if (conversationDisplayMode !== IConversationDisplayMode.All) {
                    dispatch(setSelectedConversation(''));
                }
            })
            .catch((e: Error) => {
                const errorMessage = e instanceof Error ? e.message : String(e);
                throw new Error(errorMessage);
            });
    };

    const handleHideChat = () => {
        onHideChat().catch((e: Error) => {
            const errorMessage = `Unable to hide chat. Details: ${e instanceof Error ? e.message : String(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    };

    const countActiveChats = () => {
        let activeChats = 0;
        Object.keys(conversations).forEach((key) => {
            if (!conversations[key].hidden) {
                activeChats++;
            }
        });
        return activeChats;
    };

    return countActiveChats() > 1 ? (
        <ModalDialog
            title={`Are you sure you want to hide conversation '${chatName}'?`}
            open={open}
            onClose={onClose}
            actionLabel="Hide"
            onAction={handleHideChat}
            showCancel>
            <div>
                You can restore hidden chats by:
                <ul className="max-w-md space-y-1 text-black list-disc list-inside dark:text-white/70 pl-4 pt-3">
                    <li>
                        Switching the conversation view to one of:
                        <ul className="ps-5 mt-2 space-y-1 list-disc list-inside">
                            <li>
                                <b>&apos;All conversations&apos;</b>
                            </li>
                            <li>
                                <b>&apos;Hidden conversations&apos;</b>
                            </li>
                        </ul>
                    </li>
                    <li>Selecting the hidden conversation</li>
                    <li>
                        Clicking the &apos;<b>Restore</b>&apos; icon.
                    </li>
                </ul>
            </div>
        </ModalDialog>
    ) : (
        <ModalDialog title="Cannot hide chat" open={false} onClose={onClose}>
            <p>
                The last active conversation cannot be hidden. If you want to hide this conversation, please // create a
                new one first and then try again.
            </p>
        </ModalDialog>
    );
};
